import { Search } from 'lucide-react'
import { useQueryState } from 'nuqs'
import { useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'

import { AlphabetPaginator } from '@/components/AlphabetPaginator/AlphabetPaginator'
import { Doc } from '@/components/documents/documentUtils'
import { PageHeader } from '@/components/ds'
import { Input } from '@/components/ui/input'
import { Contacts } from '@/containers/Contacts'
import { balancesByCounterparty } from '@/containers/Contacts/contactUtils'
import { useSetPageHeader } from '@/contexts/PageHeaderContext'
import { apiFetcher } from '@/data/fetcher'
import { usePagination } from '@/hooks/usePagination'
import { Collection } from '@/interfaces/Collection'
import { Participant } from '@/interfaces/Participant'
import { Settings } from '@/interfaces/Settings'

const plural = (n: number) => (n === 1 ? 'kontakt' : n >= 2 && n <= 4 ? 'kontakty' : 'kontaktů')

export default function ContactsPage() {
  const pagination = usePagination({ perPage: 20 })
  const { setTotal, setCurrentPage } = pagination

  const [letter, setLetter] = useQueryState('letter')
  const [search, setSearch] = useQueryState('q')
  const [query, setQuery] = useState(search ?? '')

  // hledání s krátkým zpožděním, ať neposíláme dotaz po každém písmenu
  useEffect(() => {
    const value = query.trim()
    if (value === (search ?? '')) return
    const t = setTimeout(() => {
      setCurrentPage(1)
      if (value) setLetter(null)
      setSearch(value === '' ? null : value)
    }, 250)
    return () => clearTimeout(t)
  }, [query]) // eslint-disable-line react-hooks/exhaustive-deps

  const { data: participants, isLoading } = useSWR<Collection<Participant>>(
    `/api/v2/contacts?limit=${pagination.limit}&offset=${pagination.offset}${
      letter === null ? '' : `&letter=${encodeURIComponent(letter)}`
    }${search === null ? '' : `&q=${encodeURIComponent(search)}`}`,
    apiFetcher,
    { keepPreviousData: true },
  )

  const { data: letters } = useSWR<Collection<string>>(`/api/v2/contacts/tabs`, apiFetcher)
  const { data: settings } = useSWR<Settings>('/api/v2/settings', apiFetcher)
  const primary = settings?.primaryCurrency ?? 'CZK'

  // Dlužné částky: faktury za letošní a loňský rok (stejná data jako seznam faktur, sdílená cache).
  // Celkový zůstatek přes všechny roky je v detailu kontaktu.
  const year = new Date().getFullYear()
  const { data: invoicesThisYear, isLoading: l1 } = useSWR<Doc[]>(`/api/v2/invoices?year=${year}&limit=1000`, apiFetcher)
  const { data: invoicesLastYear, isLoading: l2 } = useSWR<Doc[]>(`/api/v2/invoices?year=${year - 1}&limit=1000`, apiFetcher)
  const balances = useMemo(
    () => balancesByCounterparty([...(invoicesThisYear ?? []), ...(invoicesLastYear ?? [])], 'customer'),
    [invoicesThisYear, invoicesLastYear],
  )

  useEffect(() => {
    if (participants) setTotal(participants.total)
  }, [participants?.total]) // eslint-disable-line react-hooks/exhaustive-deps

  useSetPageHeader({
    breadcrumbs: [{ href: '/contacts', children: 'Kontakty' }],
    newButton: 'participant',
  })

  const filtered = letter !== null || search !== null
  const clearFilter = () => {
    setLetter(null)
    setSearch(null)
    setQuery('')
    setCurrentPage(1)
  }

  const total = participants?.total

  return (
    <>
      <title>Kontakty</title>
      <div className="mx-auto w-full max-w-[1280px]">
        <PageHeader
          title="Kontakty"
          description={total === undefined ? 'Odběratelé a dodavatelé' : `${total} ${plural(total)}${filtered ? ' odpovídá filtru' : ''}`}
          actions={
            <div className="relative w-full sm:w-72">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Název, IČO nebo e-mail"
                aria-label="Hledat kontakty"
                className="pl-8"
              />
            </div>
          }
        />

        {letters && letters.data.length > 1 && (
          <AlphabetPaginator
            className="mb-3"
            letters={letters.data}
            onChange={() => {
              setCurrentPage(1)
              if (search !== null) {
                setSearch(null)
                setQuery('')
              }
            }}
          />
        )}

        <Contacts
          contacts={participants}
          isLoading={isLoading && !participants}
          pagination={pagination}
          balances={balances}
          balancesLoading={l1 || l2}
          primaryCurrency={primary}
          filtered={filtered}
          onClearFilter={clearFilter}
        />
      </div>
    </>
  )
}
